import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { companyService } from '@/services/company.service'
import { billingService } from '@/services/billing.service'
import { subscriptionPlanSchema, type SubscriptionPlan } from '@/types/billing.types'
import { getPlanFeatures, getBankBadgeText } from '@/utils/planFeatures'
import type { User } from '@/types/auth.types'
import { Button } from '@/components/ui/Button'
import {
  ArrowRight,
  ArrowUpRight,
  Activity,
  Clock,
  LineChart,
  Check,
  TrendingUp,
  Users,
  Calculator,
  BarChart3,
  Target,
  Sparkles,
  Landmark,
  Info,
  Menu,
} from "lucide-react";
import { SevenAssistantFloating } from "@/components/seven-assistant-floating";
import { DemoModal, openDemoModal } from "@/components/demo-modal";
import { ExecutiveHero } from "@/components/landing/templates/ExecutiveHero";
import { ExecutiveGovernance } from "@/components/landing/templates/ExecutiveGovernance";
import { getCustomLandingDescriptor } from "@/components/landing/registry/CustomLandingRegistry";

import { useScrollReveal } from "@/hooks/use-scroll-reveal";
import { useTracking } from '@/hooks/useTracking';
import { useDomainBranding } from '@/hooks/useDomainBranding';
import { useTheme } from '@/context/ThemeContext';
import { resolveImageUrl } from '@/utils/imageUrl';
import iconeNavbarDark from '@/assets/logos/icone_navbar_dark.png';
import { SeoHead } from '@/components/seo/SeoHead';
import {
  HOME_SEO_METADATA,
  buildPageJsonLd,
  type SeoPageDefinition,
} from '@/data/seoArchitecture';
import './LandingPage.css';

export interface LandingPageProps {
  seoPageOverride?: SeoPageDefinition;
  initialSectionId?: string;
}

export function LandingPage({ seoPageOverride, initialSectionId }: LandingPageProps = {}) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { resolution, branding, error, isTenantResolved } = useDomainBranding()
  const { isDark } = useTheme()

  useScrollReveal();

  useEffect(() => {
    if (!initialSectionId) return;
    const timer = window.setTimeout(() => {
      const targetEl = document.getElementById(initialSectionId);
      if (targetEl && typeof targetEl.scrollIntoView === 'function') {
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 80);
    return () => window.clearTimeout(timer);
  }, [initialSectionId]);

  // Rastreamento seguro (GTM & Meta Pixel)
  useEffect(() => {
    if (branding?.landing_gtm_id && /^GTM-[A-Z0-9]{4,15}$/.test(branding.landing_gtm_id)) {
      const scriptId = 'seven-gtm-script';
      if (!document.getElementById(scriptId)) {
        const script = document.createElement('script');
        script.id = scriptId;
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtm.js?id=${branding.landing_gtm_id}`;
        document.head.appendChild(script);
      }
    }
  }, [branding?.landing_gtm_id]);

  useEffect(() => {
    if (branding?.landing_meta_pixel_id && /^[0-9]{8,25}$/.test(branding.landing_meta_pixel_id)) {
      const scriptId = 'seven-pixel-script';
      if (!document.getElementById(scriptId)) {
        const script = document.createElement('script');
        script.id = scriptId;
        script.text = `
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${branding.landing_meta_pixel_id}');
          fbq('track', 'PageView');
        `;
        document.head.appendChild(script);
      }
    }
  }, [branding?.landing_meta_pixel_id]);

  async function handleSmartRedirect() {
    try {
      const companies = await companyService.list()
      if (companies.length === 0) {
        navigate('/dashboard/company-setup', { replace: true })
        return
      }
      const isPartner = companies.some(c => c.user_role === 'partner')
      if (isPartner) {
        navigate('/partner', { replace: true })
      } else {
        navigate('/dashboard', { replace: true })
      }
    } catch (e) {
      navigate('/dashboard/company-setup', { replace: true })
    }
  }

  if (!resolution.isMainDomain && error) {
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center p-6 text-center">
        <div className="w-16 h-16 rounded-full bg-surface-2 border border-border flex items-center justify-center mb-6">
          <Info className="w-8 h-8 text-text-3" />
        </div>
        <h1 className="font-display font-bold text-2xl md:text-3xl text-foreground mb-3">
          Organização não encontrada
        </h1>
        <p className="text-text-3 max-w-md text-sm md:text-base mb-8">
          O endereço informado não corresponde a uma organização ativa no sistema.
        </p>
        <Button
          onClick={() => { window.location.href = window.location.origin }}
          className="bg-primary text-white hover:bg-primary-hover px-6 py-2.5 text-sm font-semibold"
        >
          Ir para o início
        </Button>
      </div>
    )
  }

  const isWhiteLabel = isTenantResolved || resolution.isTenantSubdomain || resolution.isCustomDomain
  const brandName = branding?.brand_name || branding?.company_name || 'Seven Analytics'
  const tenantLogoUrl = isDark
    ? (branding?.logo_dark_url || branding?.logo_light_url)
    : branding?.logo_light_url
  const logoUrl = tenantLogoUrl
    ? resolveImageUrl(tenantLogoUrl)
    : (isDark ? iconeNavbarDark : '/logo-light.png')
  const tenantSlug = resolution.slug || ''
  const loginUrl = isWhiteLabel ? (tenantSlug ? `/login?tenant=${tenantSlug}` : '/login') : '/login'
  const contactEmail = branding?.landing_contact_email || 'contato@sevenbpo.com.br'
  const contactPhone = branding?.landing_contact_phone || (isWhiteLabel ? null : '11 91633-8874')
  const layoutTemplate = branding?.landing_layout_template || 'default'

  // ── MODO 1: PORTAL DIRETO DE LOGIN ──
  if (layoutTemplate === 'direct_login') {
    if (typeof window !== 'undefined' && window.location.pathname === '/') {
      window.location.replace(loginUrl);
    }
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center p-6 text-center" data-testid="landing-direct-login">
        <p className="text-text-2 text-sm mb-3">Redirecionando para a área restrita de autenticação...</p>
        <a href={loginUrl} className="text-primary text-xs hover:underline">
          Clique aqui se não for redirecionado automaticamente
        </a>
      </div>
    );
  }

  // ── MODO 2: REDIRECIONAMENTO EXTERNO PARA SITE OFICIAL DO PARCEIRO ──
  if (layoutTemplate === 'external_redirect') {
    if (branding?.landing_custom_url && typeof window !== 'undefined') {
      window.location.replace(branding.landing_custom_url);
    }
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center p-6 text-center" data-testid="landing-external-redirect">
        <p className="text-text-2 text-sm mb-3">Redirecionando para o site oficial...</p>
        {branding?.landing_custom_url && (
          <a href={branding.landing_custom_url} className="text-primary text-xs hover:underline">
            Clique aqui se não for redirecionado automaticamente
          </a>
        )}
      </div>
    );
  }

  // ── MODO 3: LANDING PAGE SOB MEDIDA (CUSTOM) ──
  if (layoutTemplate === 'custom') {
    const customDescriptor = getCustomLandingDescriptor(branding?.landing_custom_template_key);
    const CustomComponent = customDescriptor?.component;

    if (CustomComponent) {
      return (
        <div className="landing-root min-h-screen bg-background text-foreground" data-testid="landing-custom-layout">
          <CustomComponent
            branding={branding || ({} as any)}
            isWhiteLabel={isWhiteLabel}
            tenantSlug={tenantSlug}
            loginUrl={loginUrl}
            contactEmail={contactEmail}
            contactPhone={contactPhone}
            onOpenDemoModal={openDemoModal}
          />
          {(!isWhiteLabel || Boolean(branding?.landing_show_assistant)) && <SevenAssistantFloating />}
          <DemoModal />
        </div>
      );
    }

    return (
      <div className="landing-root min-h-screen bg-background text-foreground overflow-x-hidden selection:bg-bronze selection:text-white" data-testid="landing-custom-layout">
        <Nav
          user={user}
          handleSmartRedirect={handleSmartRedirect}
          isWhiteLabel={isWhiteLabel}
          brandName={brandName}
          logoUrl={logoUrl}
          loginUrl={loginUrl}
          isExecutive={true}
        />
        <main id="main-content">
          <ExecutiveHero
            brandName={brandName}
            heroTag={branding?.landing_hero_tag || `EXCLUSIVO — ${customDescriptor?.name || brandName}`}
            headline={branding?.landing_headline || `${brandName} — Soluções Sob Medida`}
            subheadline={branding?.landing_subheadline}
            ctaText={branding?.landing_cta_text || 'Solicitar Acesso'}
            metrics={branding?.landing_badge_metrics}
            contactPhone={contactPhone}
            contactEmail={contactEmail}
            onCtaClick={openDemoModal}
          />
          <ExecutiveGovernance brandName={brandName} />
          <Platform brandName={brandName} sectionNumber="03 — PLATAFORMA" />
          <Intelligence brandName={brandName} isWhiteLabel={isWhiteLabel} sectionNumber="04 — ASSISTENTE" />
          <Plans
            isWhiteLabel={isWhiteLabel}
            tenantSlug={tenantSlug}
            brandName={brandName}
            sectionNumber="05 — PLANOS"
            isExecutive={true}
            onOpenDemoModal={openDemoModal}
          />
          <CTA
            isWhiteLabel={isWhiteLabel}
            brandName={brandName}
            contactPhone={contactPhone}
            contactEmail={contactEmail}
            ctaText={branding?.landing_cta_text}
            isExecutive={true}
            onCtaClick={openDemoModal}
          />
        </main>
        <Footer
          isWhiteLabel={isWhiteLabel}
          brandName={brandName}
          tenantSlug={tenantSlug}
          loginUrl={loginUrl}
          contactEmail={contactEmail}
          contactPhone={contactPhone}
          isExecutive={true}
        />
        {(!isWhiteLabel || Boolean(branding?.landing_show_assistant)) && <SevenAssistantFloating />}
        <DemoModal />
      </div>
    );
  }

  // ── MODO 4: MODERN TECH SPLIT ──
  if (layoutTemplate === 'modern_split') {
    return (
      <div className="landing-root min-h-screen bg-background text-foreground overflow-x-hidden selection:bg-bronze selection:text-white" data-testid="landing-modern-split-layout">
        <Nav
          user={user}
          handleSmartRedirect={handleSmartRedirect}
          isWhiteLabel={isWhiteLabel}
          brandName={brandName}
          logoUrl={logoUrl}
          loginUrl={loginUrl}
          isExecutive={false}
        />
        <main id="main-content">
          <Hero
            isWhiteLabel={isWhiteLabel}
            brandName={brandName}
            branding={branding}
            loginUrl={loginUrl}
          />
          <Logos brandName={brandName} />
          <Platform brandName={brandName} sectionNumber="02 — RECURSOS" />
          <Intelligence brandName={brandName} isWhiteLabel={isWhiteLabel} sectionNumber="03 — INTELIGÊNCIA" />
          <Plans
            isWhiteLabel={isWhiteLabel}
            tenantSlug={tenantSlug}
            brandName={brandName}
            sectionNumber="04 — PLANOS"
            isExecutive={false}
            onOpenDemoModal={openDemoModal}
          />
          <CTA
            isWhiteLabel={isWhiteLabel}
            brandName={brandName}
            contactPhone={contactPhone}
            contactEmail={contactEmail}
            ctaText={branding?.landing_cta_text}
            isExecutive={false}
            onCtaClick={openDemoModal}
          />
        </main>
        <Footer
          isWhiteLabel={isWhiteLabel}
          brandName={brandName}
          tenantSlug={tenantSlug}
          loginUrl={loginUrl}
          contactEmail={contactEmail}
          contactPhone={contactPhone}
          isExecutive={false}
        />
        {(!isWhiteLabel || Boolean(branding?.landing_show_assistant)) && <SevenAssistantFloating />}
        <DemoModal />
      </div>
    );
  }

  const activeSeoPage = seoPageOverride || HOME_SEO_METADATA;

  const seoHeadElement = isWhiteLabel ? (
    <SeoHead
      title={branding?.brand_name ? `${branding.brand_name} — Portal Financeiro` : `${brandName} — Portal Financeiro`}
      description={branding?.landing_subheadline || `Plataforma de inteligência e gestão financeira ${brandName}.`}
      canonicalPath={activeSeoPage.path}
      isWhiteLabel={true}
      brandName={brandName}
    />
  ) : (
    <SeoHead
      title={activeSeoPage.metaTitle}
      description={activeSeoPage.metaDescription}
      canonicalPath={activeSeoPage.path}
      jsonLd={buildPageJsonLd(activeSeoPage)}
      isWhiteLabel={false}
    />
  );

  if (layoutTemplate === 'executive') {
    return (
      <div className="landing-root min-h-screen bg-background text-foreground overflow-x-hidden selection:bg-bronze selection:text-white" data-testid="landing-executive-layout">
        {seoHeadElement}
        <Nav
          user={user}
          handleSmartRedirect={handleSmartRedirect}
          isWhiteLabel={isWhiteLabel}
          brandName={brandName}
          logoUrl={logoUrl}
          loginUrl={loginUrl}
          isExecutive={true}
        />
        <main id="main-content">
          <ExecutiveHero
            brandName={brandName}
            heroTag={branding?.landing_hero_tag}
            headline={branding?.landing_headline}
            subheadline={branding?.landing_subheadline}
            ctaText={branding?.landing_cta_text}
            metrics={branding?.landing_badge_metrics}
            contactPhone={contactPhone}
            contactEmail={contactEmail}
            onCtaClick={openDemoModal}
          />
          <ExecutiveGovernance
            brandName={brandName}
          />
          <Platform
            brandName={brandName}
            sectionNumber="03 — PLATAFORMA"
          />
          <Intelligence
            brandName={brandName}
            isWhiteLabel={isWhiteLabel}
            sectionNumber="04 — ASSISTENTE"
          />
          <Plans
            isWhiteLabel={isWhiteLabel}
            tenantSlug={tenantSlug}
            brandName={brandName}
            sectionNumber="05 — PLANOS"
            isExecutive={true}
            onOpenDemoModal={openDemoModal}
          />
          <BpoEssencial
            brandName={brandName}
            contactPhone={contactPhone}
            contactEmail={contactEmail}
            isExecutive={true}
            sectionNumber="06 — BPO FINANCEIRO"
          />
          <CTA
            isWhiteLabel={isWhiteLabel}
            brandName={brandName}
            contactPhone={contactPhone}
            contactEmail={contactEmail}
            ctaText={branding?.landing_cta_text}
            isExecutive={true}
            onCtaClick={openDemoModal}
          />
        </main>
        <Footer
          isWhiteLabel={isWhiteLabel}
          brandName={brandName}
          tenantSlug={tenantSlug}
          loginUrl={loginUrl}
          contactEmail={contactEmail}
          contactPhone={contactPhone}
          isExecutive={true}
        />
        {(!isWhiteLabel || Boolean(branding?.landing_show_assistant)) && <SevenAssistantFloating />}
        <DemoModal />
      </div>
    )
  }

  return (
    <div className="landing-root min-h-screen bg-background text-foreground overflow-x-hidden selection:bg-bronze selection:text-white" data-testid="landing-default-layout">
      {seoHeadElement}
      <Nav
        user={user}
        handleSmartRedirect={handleSmartRedirect}
        isWhiteLabel={isWhiteLabel}
        brandName={brandName}
        logoUrl={logoUrl}
        loginUrl={loginUrl}
        isExecutive={false}
      />
      <main id="main-content">
        <Hero
          isWhiteLabel={isWhiteLabel}
          brandName={brandName}
          branding={branding}
          loginUrl={loginUrl}
        />
        <Logos brandName={brandName} />
        <Platform
          brandName={brandName}
          sectionNumber="02 — PLATAFORMA"
        />
        <Intelligence
          brandName={brandName}
          isWhiteLabel={isWhiteLabel}
          sectionNumber="03 — ASSISTENTE"
        />
        <BPO
          brandName={brandName}
          isWhiteLabel={isWhiteLabel}
          sectionNumber="04 — OPERAÇÃO"
        />
        <Contrast
          brandName={brandName}
          sectionNumber="05 — CONTRASTE"
        />
        <Plans
          isWhiteLabel={isWhiteLabel}
          tenantSlug={tenantSlug}
          brandName={brandName}
          sectionNumber="06 — PLANOS"
          isExecutive={false}
          onOpenDemoModal={openDemoModal}
        />
        <BpoEssencial
          brandName={brandName}
          contactPhone={contactPhone}
          contactEmail={contactEmail}
          isExecutive={false}
          sectionNumber="07 — BPO FINANCEIRO"
        />
        <CTA
          isWhiteLabel={isWhiteLabel}
          brandName={brandName}
          contactPhone={contactPhone}
          contactEmail={contactEmail}
          ctaText={branding?.landing_cta_text}
          isExecutive={false}
          onCtaClick={openDemoModal}
        />
      </main>
      <Footer
        isWhiteLabel={isWhiteLabel}
        brandName={brandName}
        tenantSlug={tenantSlug}
        loginUrl={loginUrl}
        contactEmail={contactEmail}
        contactPhone={contactPhone}
        isExecutive={false}
      />
      {(!isWhiteLabel || Boolean(branding?.landing_show_assistant)) && <SevenAssistantFloating />}
      <DemoModal />
    </div>
  );
}

/* ============================================================
   NAV
   ============================================================ */
interface NavProps {
  user: User | null;
  handleSmartRedirect: () => Promise<void>;
  isWhiteLabel: boolean;
  brandName: string;
  logoUrl: string;
  loginUrl: string;
  isExecutive?: boolean;
}

function Nav({ user, handleSmartRedirect, isWhiteLabel, brandName, logoUrl, loginUrl, isExecutive }: NavProps) {
  const [logoError, setLogoError] = useState<boolean>(false);
  const { isDark } = useTheme();
  const { trackEvent } = useTracking();

  useEffect(() => {
    setLogoError(false);
  }, [logoUrl]);

  const shouldRenderLogo = !logoError && Boolean(logoUrl);
  const isDefaultLightLogo = !isWhiteLabel && logoUrl === '/logo-light.png';
  const isDefaultDarkLogo = !isWhiteLabel && logoUrl === iconeNavbarDark;
  const logoWidth = isDefaultDarkLogo ? 120 : 106;
  const logoHeight = isDefaultDarkLogo ? 44 : 28;
  const logoStyle = isDefaultLightLogo
    ? { width: '106px', height: '28px' }
    : isDefaultDarkLogo
      ? { width: '120px', height: '44px' }
      : undefined;
  const logoClassName = isDefaultLightLogo
    ? "h-7 w-[106px] object-contain max-h-7"
    : isDefaultDarkLogo
      ? "h-11 w-[120px] object-contain max-h-11"
      : "h-7 w-auto object-contain max-h-7";
  const ctaButtonClass = isDark
    ? "bg-bronze hover:bg-bronze-hover text-white"
    : "bg-foreground hover:bg-ink text-white";

  const allLinks = isExecutive
    ? [
        ["Governança", "#governanca"],
        ["Plataforma", "#plataforma"],
        ["Assistente", "#inteligencia"],
        ["BPO Financeiro", "#bpo-essencial"],
        ["Contato", "#contato"],
      ]
    : [
        ["Plataforma", "#plataforma"],
        ["Assistente", "#inteligencia"],
        ["Operação", "#bpo"],
        ["Planos", "#planos"],
        ["BPO Financeiro", "#bpo-essencial"],
        ["Contato", "#contato"],
      ];

  const links = allLinks.filter(([l]) => !(isWhiteLabel && l === "Planos"));

  return (
    <header className="fixed top-0 inset-x-0 z-50 glass-navbar">
      <div className="max-w-[1360px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <a href="#top" className="inline-flex items-center gap-2.5 py-2 min-h-[44px] group">
          {shouldRenderLogo ? (
            <img
              src={logoUrl}
              alt={`${brandName} Logo`}
              width={logoWidth}
              height={logoHeight}
              style={logoStyle}
              onError={() => setLogoError(true)}
              className={logoClassName}
            />
          ) : (
            <span className="font-display font-bold text-lg text-foreground tracking-tight">
              {brandName}
            </span>
          )}
        </a>
        <nav className="hidden lg:flex items-center gap-9 text-[13px] font-medium text-text-3">
          {links.map(([l, h]) => (
            <a
              key={l}
              href={h}
              className="hover:text-foreground transition-colors"
            >
              {l}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <Button
              onClick={() => void handleSmartRedirect()}
              className={`group inline-flex items-center gap-2 px-5 py-2.5 text-[13px] font-semibold transition ${ctaButtonClass}`}
            >
              Acessar painel
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition" />
            </Button>
          ) : (
            <>
              <Link
                to={loginUrl}
                className="inline-flex items-center px-3 py-2 min-h-[44px] text-[13px] font-semibold text-text-3 hover:text-foreground transition mr-2"
              >
                Entrar
              </Link>
              <Button
                onClick={() => {
                  trackEvent({ eventType: 'CTA_CLICK', elementId: 'nav_demo' });
                  if (isWhiteLabel) {
                    const el = document.getElementById('contato') || document.getElementById('bpo-essencial');
                    el?.scrollIntoView({ behavior: 'smooth' });
                  } else {
                    openDemoModal();
                  }
                }}
                className={`group hidden sm:inline-flex items-center gap-2 px-5 py-2.5 text-[13px] font-semibold transition ${ctaButtonClass}`}
              >
                {isWhiteLabel ? 'Fale Conosco' : 'Agendar demonstração'}
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition" />
              </Button>
            </>
          )}
          <Button
            variant="ghost"
            aria-label="Abrir menu de navegação"
            className="lg:hidden text-text-3 p-2 min-h-[44px] min-w-[44px]"
          >
            <Menu className="w-5 h-5" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </header>
  );
}

/* ============================================================
   HERO
   ============================================================ */
interface HeroProps {
  isWhiteLabel: boolean;
  brandName: string;
  branding: any;
  loginUrl: string;
}

function Hero({ isWhiteLabel, brandName, branding, loginUrl }: HeroProps) {
  const { trackEvent } = useTracking();
  const navigate = useNavigate();

  const ctaLabel = branding?.landing_cta_text || (isWhiteLabel ? 'Acessar Plataforma' : 'Agendar demonstração');

  return (
    <section
      id="top"
      className="relative pt-32 md:pt-40 pb-20 md:pb-28 px-6 lg:px-10 border-b border-border overflow-hidden"
    >
      <div className="absolute inset-0 hero-warm-glow pointer-events-none opacity-70" />
      {/* editorial section number */}
      <div data-nosnippet="" className="absolute top-24 right-6 lg:right-10 text-[11px] font-display font-semibold tracking-[0.35em] text-bronze">
        01 — INÍCIO
      </div>

      <div className="relative max-w-[1360px] mx-auto grid lg:grid-cols-[1.05fr_1fr] gap-14 lg:gap-16 items-center">
        {/* Left copy */}
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-border-strong bg-primary-light/70 text-bronze text-[10px] font-semibold tracking-[0.28em] uppercase">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-bronze opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-bronze" />
            </span>
            {isWhiteLabel ? `Solução Financeira Inteligente • ${brandName}` : 'A plataforma financeira que trabalha com você'}
          </div>

          <h1 className="mt-8 font-display font-bold text-[2rem] sm:text-4xl lg:text-[3.75rem] leading-[0.98] tracking-[-0.03em] text-foreground">
            {branding?.landing_headline ? (
              branding.landing_headline
            ) : isWhiteLabel ? (
              <>
                Gestão Financeira
                <br />
                & <span className="text-bronze">Inteligência</span> —
                <br />
                para o seu <span className="italic font-light text-text-3">negócio</span>.
              </>
            ) : (
              <>
                Menos planilha.
                <br />
                Mais <span className="text-bronze">decisão</span> —
                <br />
                no <span className="italic font-light text-text-3">seu</span> tempo.
              </>
            )}
          </h1>

          <p className="mt-8 max-w-xl text-lg text-text-3 leading-relaxed">
            {branding?.landing_subheadline ? (
              branding.landing_subheadline
            ) : isWhiteLabel ? (
              `A ${brandName} organiza o seu financeiro, cuida da operação e mostra o resultado em tempo real. Você para de passar o dia amarrado no operacional — e volta a decidir com clareza.`
            ) : (
              `A Seven organiza o seu financeiro, cuida da operação e mostra o resultado em tempo real. Você para de passar o dia amarrado no operacional — e volta a decidir com clareza. Para empresas, contadores e escritórios que cuidam de vários clientes.`
            )}
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <Button
              onClick={() => {
                trackEvent({ eventType: 'CTA_CLICK', elementId: 'hero_demo' });
                if (isWhiteLabel) {
                  navigate(loginUrl);
                } else {
                  openDemoModal();
                }
              }}
              className="group inline-flex items-center gap-2 bg-bronze hover:bg-bronze-hover text-white px-8 py-4 text-[13px] font-semibold tracking-wide transition-all hover:-translate-y-0.5 shadow-[var(--shadow-bronze)]"
            >
              {ctaLabel}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition" />
            </Button>
            <a
              href="#plataforma"
              onClick={() => trackEvent({ eventType: 'CTA_CLICK', elementId: 'hero_how_it_works' })}
              className="inline-flex items-center gap-2 border border-border-strong text-foreground px-8 py-4 text-[13px] font-semibold tracking-wide hover:bg-surface-2 transition-all"
            >
              Ver como funciona
            </a>
          </div>

          <div className="mt-14 pt-8 border-t border-border grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl">
            {[
              [Sparkles, "Conciliação", "em minutos"],
              [Users, "Multi-empresa,", "em um só painel"],
              [LineChart, "Resultado", "em tempo real"],
              [Clock, "Vários clientes,", "um só acesso"],
            ].map(([Icon, l1, l2], i) => (
              <div key={i} className="flex flex-col gap-2">
                <Icon className="w-5 h-5 text-bronze" strokeWidth={1.6} />
                <span className="text-[12px] text-text-3 leading-tight font-medium">
                  {l1 as string}
                  <br />
                  {l2 as string}
                </span>
              </div>
            ))}
          </div>

        </div>

        {/* Right — devices */}
        <div className="relative">
          <div className="absolute -inset-10 rounded-full blur-3xl opacity-40 pointer-events-none bg-primary-light" />
          <DeviceShowcase />
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   DEVICES — light-themed dashboard inside dark framed devices
   ============================================================ */
function DeviceShowcase() {
  return (
    <div className="relative mx-auto max-w-xl lg:max-w-none pb-8">
      <div className="relative">
        <div className="float-laptop origin-center">
          <TabletMockup />
        </div>

        <div className="mt-8 lg:mt-0 lg:absolute lg:-bottom-6 lg:-right-2 lg:w-[210px] mx-auto max-w-[220px] lg:mx-0 float-phone origin-center lg:rotate-[-4deg]">
          <PhoneMockup />
        </div>
        {/* shared floor shadow */}
        <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-0 w-[85%] h-8 blur-2xl opacity-40 device-floor-shadow" />
      </div>
    </div>
  );
}

function TabletMockup() {
  return (
    <div className="relative mx-auto w-full max-w-5xl">
      {/* iPad-style frame */}
      <div className="relative p-[14px] md:p-[18px] tablet-mockup-frame">
        {/* front camera */}
        <div className="absolute top-1/2 -translate-y-1/2 left-[6px] md:left-[8px] w-1.5 h-1.5 rounded-full bg-foreground/20 ring-1 ring-white/10">
          <div className="w-[3px] h-[3px] mx-auto mt-[1.5px] rounded-full bg-ink" />
        </div>

        <div className="relative rounded-[14px] overflow-hidden bg-surface aspect-[16/11]">
          <LaptopDashboard />
          {/* glass reflection */}
          <div className="pointer-events-none absolute inset-0 glass-reflection" />
        </div>
      </div>

      {/* contact shadow */}
      <div className="pointer-events-none mx-auto mt-3 h-2 blur-md opacity-50 tablet-mockup-shadow" />
    </div>
  );
}

function LaptopDashboard() {
  const nav = [
    "Visão Geral",
    "Dashboard",
    "Fluxo de Caixa",
    "Receitas",
    "Despesas",
    "Projetos",
    "Clientes",
    "Relatórios",
  ];
  return (
    <div className="text-left h-full flex text-[10px] md:text-[11px] bg-surface">
      <aside className="w-[110px] md:w-[150px] border-r border-border bg-surface-2/50 p-3 md:p-4 flex flex-col gap-1 shrink-0">
        <div className="font-display font-bold text-bronze tracking-[0.28em] mb-4 text-[10px]">
          SEVEN
        </div>
        {nav.map((n) => (
          <div
            key={n}
            className={`px-2 py-1.5 truncate ${
              n === "Dashboard"
                ? "bg-primary-light text-bronze font-semibold"
                : "text-text-3"
            }`}
          >
            {n}
          </div>
        ))}
      </aside>

      <div className="flex-1 p-3 md:p-5 space-y-3 md:space-y-4 min-w-0">
        <div className="flex items-baseline justify-between">
          <div className="font-display font-bold text-[13px] md:text-base text-foreground">
            Dashboard
          </div>
          <div className="text-[8px] md:text-[9px] tracking-[0.2em] uppercase text-text-3">
            Nov / 2025
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 md:gap-3">
          {[
            ["Receita Líquida", "R$ 1.280.430", "+11,3%"],
            ["Lucro Operacional", "R$ 215.430", "+9,7%"],
            ["Margem Líquida", "16,8%", "+2,8%"],
          ].map(([l, v, d]) => (
            <div
              key={l}
              className="border border-border p-2 md:p-3 bg-surface"
            >
              <div className="uppercase tracking-[0.2em] text-text-3 text-[8px]">
                {l}
              </div>
              <div className="mt-1 font-display font-bold text-[11px] md:text-sm text-foreground truncate">
                {v}
              </div>
              <div className="text-bronze text-[8px] md:text-[9px] font-semibold">
                {d} vs mês anterior
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-2 md:gap-3">
          <div className="col-span-2 border border-border p-2 md:p-3 bg-surface">
            <div className="flex items-center justify-between mb-1">
              <div className="uppercase tracking-[0.2em] text-text-3 text-[8px]">
                Fluxo Projetado
              </div>
              <div className="text-bronze text-[8px] font-semibold">
                Previsto × Realizado
              </div>
            </div>
            <div className="h-16 md:h-24">
              <MiniChart />
            </div>
          </div>
          <div className="border border-border p-2 md:p-3 bg-primary-light/50 flex flex-col justify-center">
            <div className="uppercase tracking-[0.2em] text-text-3 text-[8px]">
              Delta previsto
            </div>
            <div className="mt-1 font-display font-bold text-[13px] md:text-base text-bronze-hover">
              -11,7%
            </div>
            <div className="text-text-3 text-[8px]">vs previsto</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 md:gap-3">
          <div className="border border-border p-2 md:p-3 bg-surface">
            <div className="uppercase tracking-[0.2em] text-text-3 text-[8px] mb-1">
              Custos por Centro
            </div>
            <div className="flex items-center gap-2">
              <PieMini />
              <div className="space-y-0.5 text-[8px] text-text-2">
                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-bronze" />
                  Operacional 40%
                </div>
                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-bronze/60" />
                  Marketing 20%
                </div>
                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-bronze/30" />
                  Comercial 20%
                </div>
              </div>
            </div>
          </div>
          {[
            ["LTV Clientes", "R$ 2.850", "+5,4%"],
            ["LTV Funcionários", "R$ 18.650", "+8,7%"],
          ].map(([l, v, d]) => (
            <div
              key={l}
              className="border border-border p-2 md:p-3 bg-surface"
            >
              <div className="uppercase tracking-[0.2em] text-text-3 text-[8px]">
                {l}
              </div>
              <div className="mt-1 font-display font-bold text-[11px] md:text-sm text-foreground">
                {v}
              </div>
              <div className="text-bronze text-[8px] font-semibold">{d}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PieMini() {
  return (
    <svg viewBox="0 0 32 32" className="w-10 h-10 md:w-12 md:h-12 shrink-0 -rotate-90">
      <circle r="16" cx="16" cy="16" fill="var(--bg-2)" />
      <circle
        r="8"
        cx="16"
        cy="16"
        fill="transparent"
        stroke="var(--bronze)"
        strokeWidth="16"
        strokeDasharray="20.1 50.3"
      />
      <circle
        r="8"
        cx="16"
        cy="16"
        fill="transparent"
        stroke="var(--bronze-hover)"
        strokeWidth="16"
        strokeDasharray="10.05 50.3"
        strokeDashoffset="-20.1"
      />
      <circle
        r="8"
        cx="16"
        cy="16"
        fill="transparent"
        stroke="var(--primary-light)"
        strokeWidth="16"
        strokeDasharray="10.05 50.3"
        strokeDashoffset="-30.15"
      />
    </svg>
  );
}

function PhoneMockup() {
  return (
    <div className="relative mx-auto" style={{ width: "100%", maxWidth: "260px" }}>
      {/* titanium outer frame */}
      <div className="relative border border-border phone-mockup-frame">
        {/* inner black bezel */}
        <div
          className="relative bg-ink"
          style={{
            borderRadius: "2.45rem",
            padding: "8px",
          }}
        >

          <div className="relative rounded-[2rem] overflow-hidden bg-surface aspect-[9/19.5]">
            {/* Dynamic Island */}
            <div className="absolute top-[7px] left-1/2 -translate-x-1/2 w-[74px] h-[22px] rounded-full bg-black z-20 flex items-center justify-end px-2 gap-1">
              <div className="w-1.5 h-1.5 rounded-full bg-ink ring-[0.5px] ring-border" />
            </div>
            {/* status bar */}
            <div className="absolute top-0 left-0 right-0 h-[34px] z-10 flex items-center justify-between px-4 text-[9px] font-semibold text-foreground/80">
              <span>9:41</span>
              <span className="w-8" />
            </div>

            <div className="pt-10 px-3 pb-3 h-full flex flex-col gap-2.5 text-left">
              <div className="text-center pt-1">
                <div className="font-display font-bold text-sm text-foreground">
                  Olá, CEO
                </div>
                <div className="text-[9px] text-text-3 leading-tight mt-0.5">
                  Resumo do seu negócio hoje
                </div>
              </div>
              {[
                ["Saldo em Caixa", "R$ 312.430", "+17,8%"],
                ["Receita Líquida", "R$ 1.280.430", "+11,3%"],
                ["Lucro Operacional", "R$ 215.430", "+9,7%"],
              ].map(([l, v, d]) => (
                <div
                  key={l}
                  className="border border-border bg-surface p-3 text-center"
                >
                  <div className="uppercase tracking-[0.2em] text-text-3 text-[8px]">
                    {l}
                  </div>
                  <div className="mt-1 font-display font-bold text-sm text-foreground">
                    {v}
                  </div>
                  <div className="text-bronze text-[8px] mt-0.5 font-semibold">
                    {d} vs mês
                  </div>
                </div>
              ))}
              {/* home indicator */}
              <div className="mt-auto mx-auto w-[80px] h-[3px] rounded-full bg-foreground/40" />
            </div>

            {/* screen reflection */}
            <div className="pointer-events-none absolute inset-0 phone-inner-screen" />
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniChart() {
  const points = [15, 32, 24, 45, 38, 60, 52, 74, 68, 88, 80, 95];
  const max = 100;
  const path = points
    .map((p, i) => `${(i / (points.length - 1)) * 100},${100 - (p / max) * 90}`)
    .join(" ");
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
      <defs>
        <linearGradient id="g" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--bronze)" stopOpacity="0.28" />
          <stop offset="100%" stopColor="var(--bronze)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline points={`0,100 ${path} 100,100`} fill="url(#g)" stroke="none" />
      <polyline
        points={path}
        fill="none"
        stroke="var(--bronze)"
        strokeWidth="0.8"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/* ============================================================
   LOGOS
   ============================================================ */
interface LogosProps {
  brandName?: string;
}

function Logos({ brandName }: LogosProps) {
  const brands = [
    "CGM",
    "NEXUS",
    "MediaAlvo",
    "Claves",
    "Genesis",
    "Imobdesk",
  ];
  // Duplicate for seamless infinite marquee
  const loop = [...brands, ...brands];
  return (
    <section className="py-12 md:py-16 bg-surface-2 border-b border-border overflow-hidden">
      <p className="text-center text-[10px] font-semibold uppercase tracking-[0.4em] text-text-3 mb-10">
        Empresas que confiam na {brandName || 'Seven'}
      </p>
      <div className="relative w-full overflow-hidden logo-marquee-mask">
        <div className="flex items-center gap-x-12 animate-brand-marquee whitespace-nowrap w-max">
          {loop.map((b, i) => (
            <span key={i} className="inline-flex items-center gap-x-12 shrink-0">
              <span className="font-display font-bold text-sm tracking-[0.22em] text-text-3 uppercase hover:text-foreground transition-colors">
                {b}
              </span>
              <span className="w-1 h-1 rounded-full bg-border-strong opacity-50 shrink-0" />
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}


/* ============================================================
   PLATAFORMA
   ============================================================ */
interface PlatformProps {
  brandName?: string;
  sectionNumber?: string;
}

function Platform({ brandName, sectionNumber = "02 — PLATAFORMA" }: PlatformProps) {
  const features = [
    {
      title: "Você enxerga o caixa em tempo real",
      desc: "Entradas, saídas, saldo e o que ainda falta receber — atualizados no momento em que acontecem. Fim do 'espera fechar o mês pra saber'.",
    },
    {
      title: "Lançar deixa de ser tarefa chata",
      desc: "Cadastro em segundos, comprovante anexado com um arraste e as contas do mês que se repetem sozinhas. Menos digitação, mais tempo pra pensar.",
    },
    {
      title: "A conciliação anda sozinha",
      desc: "O extrato do banco chega, o assistente sugere a correspondência e você só confirma. O que tomava a manhã, agora leva minutos.",
    },
    {
      title: "O resultado do mês pronto quando você abrir",
      desc: "Receita, custo, lucro e comparativo com o que foi previsto — em uma tela clara, pronta pra apresentar pro cliente ou pro sócio.",
    },
  ];

  return (
    <section
      id="plataforma"
      className="relative py-20 md:py-24 px-6 lg:px-10 border-b border-border"
    >
      <div data-nosnippet="" className="absolute top-14 right-6 lg:right-10 text-[11px] font-display font-semibold tracking-[0.35em] text-bronze uppercase">
        {sectionNumber}
      </div>

      <div className="max-w-[1360px] mx-auto text-left">
        <div>
          <div data-nosnippet="" className="text-[11px] font-display font-semibold tracking-[0.32em] text-bronze mb-6">
            TUDO NO MESMO LUGAR
          </div>
          <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-[-0.03em]">
            O financeiro completo,
            <br />
            <span className="text-bronze">sem quebra-cabeça.</span>
          </h2>
          <p className="mt-8 text-lg text-text-3 max-w-2xl leading-relaxed">
            Você lança uma vez e a {brandName || 'Seven'} cuida do resto. O que você digita
            hoje aparece na conciliação, alimenta os relatórios e chega no
            resultado do mês — sem exportar planilha, sem retrabalho,
            sem perder o fio.
          </p>

          <div className="mt-16 grid md:grid-cols-2 gap-x-12 gap-y-10">
            {features.map((f, i) => (
              <div key={f.title} className="group cursor-pointer">
                <div className="flex items-center gap-4 mb-2">
                  <span data-nosnippet="" className="font-display text-xs font-semibold text-bronze tracking-widest tabular-nums">
                    0{i + 1}
                  </span>
                  <div className="w-10 h-px bg-border-strong group-hover:bg-bronze transition" />
                  <h3 className="font-display font-bold text-lg md:text-xl group-hover:text-bronze transition">
                    {f.title}
                  </h3>
                </div>
                <p className="text-text-3 pl-[74px]">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   INTELLIGENCE
   ============================================================ */
interface IntelligenceProps {
  brandName?: string;
  isWhiteLabel?: boolean;
  sectionNumber?: string;
}

function Intelligence({ brandName, isWhiteLabel = false, sectionNumber = "03 — ASSISTENTE" }: IntelligenceProps) {
  const assistantName = isWhiteLabel ? "o assistente inteligente" : "a Lity";

  const items = [
    [Sparkles, "Pergunte como quiser", `"Quais foram meus maiores gastos em junho?" — ${assistantName} entende e responde na hora.`],
    [Users, "Enxergue sua carteira num piscar", "\"Quantos clientes ativos eu tenho?\" com o corte por status e receita, sem montar relatório."],
    [Target, "Nada passa batido", "Mostra o que está aberto, atrasado ou esperando você — antes do cliente cobrar."],
    [Calculator, "Categoriza pra você", "Sugere onde cada lançamento se encaixa e mantém tudo no lugar certo, sem você pensar."],
    [Clock, "Fala a sua língua do tempo", "Entende \"hoje\", \"esta semana\", \"mês passado\" — sem precisar montar filtro."],
    [BarChart3, "Fecha o mês no seu ritmo", "Sugere as correspondências do extrato e transforma horas de fechamento em minutos."],
    [Activity, "Resolve várias linhas de uma vez", "Categoriza dezenas de transações em um só clique — chega de arrastar linha por linha."],
    [TrendingUp, "Transforma extrato em lançamento", "Escolhe a linha do extrato, ela vira lançamento no sistema. Simples assim."],
  ] as const;

  return (
    <section
      id="inteligencia"
      className="relative py-20 md:py-24 px-6 lg:px-10 bg-surface-2 border-b border-border"
    >
      <div data-nosnippet="" className="absolute top-14 right-6 lg:right-10 text-[11px] font-display font-semibold tracking-[0.35em] text-bronze uppercase">
        {sectionNumber}
      </div>

      <div className="max-w-[1360px] mx-auto text-left">
        <div className="max-w-3xl">
          <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-[3.5rem] leading-[0.98] tracking-[-0.03em]">
            {isWhiteLabel ? (
              <>
                Inteligência financeira que
                <br />
                <span className="text-bronze">responde na hora</span> — em português.
              </>
            ) : (
              <>
                Uma assistente que
                <br />
                <span className="text-bronze">responde na hora</span> — em português.
              </>
            )}
          </h2>
          <p className="mt-6 text-lg text-text-3 max-w-2xl leading-relaxed">
            {isWhiteLabel
              ? `Faça perguntas em linguagem natural como se estivesse falando com um consultor da ${brandName || 'nossa equipe'} — e receba a resposta na hora, direto dos números da sua empresa. Sem exportar planilhas, sem esperar fechamentos.`
              : 'Conheça a Lity. Pergunte como se estivesse falando com um colega — e receba a resposta na hora, direto dos números da sua empresa. Sem exportar planilha, sem esperar relatório.'}
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-border">
          {items.map(([Icon, title, desc]) => (
            <div
              key={title}
              className="border-r border-b border-border bg-surface p-7 group hover:bg-primary-light/50 transition-colors"
            >
              <Icon
                className="w-5 h-5 text-bronze mb-6"
                strokeWidth={1.6}
              />
              <h3 className="font-display font-bold text-lg mb-2">{title}</h3>
              <p className="text-sm text-text-3 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   BPO — dark cinematic band
   ============================================================ */
interface BPOProps {
  brandName?: string;
  isWhiteLabel?: boolean;
  sectionNumber?: string;
}

function BPO({ brandName, isWhiteLabel = false, sectionNumber = "04 — OPERAÇÃO" }: BPOProps) {
  const { trackEvent } = useTracking();
  const stats = [
    ["+30", "Empresas no mesmo painel"],
    ["1", "Login = carteira inteira"],
    ["100%", "Dados protegidos e isolados"],
    ["24/7", "Trabalho rodando pra você"],
  ];

  return (
    <section
      id="bpo"
      className="relative py-20 md:py-28 px-6 lg:px-10 text-white overflow-hidden bg-ink"
    >
      <div className="absolute right-0 top-0 w-2/3 h-full opacity-30 pointer-events-none bpo-radial-glow" />

      <div data-nosnippet="" className="absolute top-14 right-6 lg:right-10 text-[11px] font-display font-semibold tracking-[0.35em] text-bronze/80 uppercase">
        {sectionNumber}
      </div>

      <div className="relative max-w-[1360px] mx-auto grid lg:grid-cols-[1.1fr_1fr] gap-16 lg:gap-24 items-center text-left">
        <div>
          <div data-nosnippet="" className="inline-flex items-center gap-3 mb-8 text-[11px] font-display font-semibold tracking-[0.35em] text-bronze/90 uppercase">
            <span className="w-8 h-px bg-bronze/60" />
            Toda a sua carteira
          </div>
          <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-[3.75rem] leading-[0.98] tracking-[-0.03em] text-white">
            Todos os seus clientes.{" "}
            <span className="relative inline-block">
              <span className="text-bronze">Um único</span>
              <span className="absolute left-0 -bottom-1 w-full h-px bg-bronze/50" />
            </span>
            <br />
            <em className="not-italic text-bronze">acesso</em>
            <span className="text-white/45">.</span>
          </h2>
          <p className="mt-8 text-base text-white/75 max-w-lg leading-relaxed">
            Troque de empresa em um clique. Cada uma no seu espaço,
            sem confusão, sem retrabalho — e a {brandName || 'Seven'} trabalhando por baixo
            enquanto você foca no cliente.
          </p>

          <div className="mt-10 grid grid-cols-2 gap-6 max-w-lg">
            {stats.map(([v, l]) => (
              <div key={l} className="border-t border-white/20 pt-4">
                <div className="font-display font-bold text-3xl md:text-4xl text-bronze">
                  {v}
                </div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-white/60 mt-2">
                  {l}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap gap-4">
            <a
              href="#contato"
              onClick={() => trackEvent({ eventType: 'CTA_CLICK', elementId: 'bpo_specialist' })}
              className="group inline-flex items-center gap-2 px-8 py-4 text-[13px] font-semibold tracking-wide text-white transition hover:-translate-y-0.5 border border-bronze bg-bronze/20 hover:bg-bronze/40"
            >
              Falar com especialista
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
            </a>
            {!isWhiteLabel && (
              <a
                href="#planos"
                className="inline-flex items-center gap-2 border border-white/30 text-white px-8 py-4 text-[13px] font-semibold tracking-wide hover:bg-white/10 transition"
              >
                Ver planos
              </a>
            )}
          </div>
        </div>

        <div className="relative">
          {/* Corner marks */}
          <span className="absolute -top-1 -left-1 w-3 h-3 border-t border-l border-bronze/60" />
          <span className="absolute -top-1 -right-1 w-3 h-3 border-t border-r border-bronze/60" />
          <span className="absolute -bottom-1 -left-1 w-3 h-3 border-b border-l border-bronze/60" />
          <span className="absolute -bottom-1 -right-1 w-3 h-3 border-b border-r border-bronze/60" />

          <div className="relative w-full aspect-square p-8 md:p-10 overflow-hidden border border-bronze/20 bg-ink/60 backdrop-blur-sm">
            {/* Ambient bronze glow */}
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-40 blur-3xl pointer-events-none bpo-glow-effect" />
            {/* Ghost BPO */}
            <div
              aria-hidden
              className="absolute inset-x-0 bottom-4 text-center font-display font-black leading-none text-[9rem] md:text-[13rem] tracking-tighter select-none pointer-events-none bpo-ghost-text"
            >
              AUTO
            </div>

            <div className="relative h-full flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between pb-4 border-b border-white/10">
                <div className="flex items-center gap-2.5">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-success opacity-60 animate-ping" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-success" />
                  </span>
                  <span className="text-[10px] tracking-[0.3em] uppercase text-white/75">
                    Rodando por você agora
                  </span>
                </div>
                <span className="text-[10px] tracking-[0.3em] uppercase text-white/75">
                  enquanto você dorme
                </span>
              </div>

              {/* Rows */}
              <div className="flex-1 flex flex-col justify-center gap-1 my-6 text-left">
                {[
                  { n: "01", l: "Conciliações do dia", r: "realizadas", sla: "sem você lembrar" },
                  { n: "02", l: "Extrato do banco", r: "conciliado", sla: "chega sozinho" },
                  { n: "03", l: "Pagamento recebido", r: "baixado", sla: "no mesmo instante" },
                  { n: "04", l: "Novo cliente", r: "cadastrado", sla: "só com o CNPJ" },
                  { n: "05", l: "Endereço completo", r: "preenchido", sla: "só com o CEP" },
                  { n: "06", l: "Contas recorrentes", r: "lançadas", sla: "todo mês, sozinhas" },
                ].map((row, i, arr) => (
                  <div
                    key={row.l}
                    className={`group flex items-center gap-4 py-2.5 ${
                      i < arr.length - 1 ? "border-b border-white/[0.06]" : ""
                    } hover:bg-white/[0.02] transition-colors`}
                  >
                    <span className="text-[10px] font-display tabular-nums text-white/70 tracking-wider">
                      {row.n}
                    </span>
                    <span className="flex-1 text-[13px] md:text-sm text-white/90 group-hover:text-white transition">
                      {row.l}
                    </span>
                    <span className="text-[9px] tracking-[0.2em] uppercase text-white/75 tabular-nums">
                      {row.sla}
                    </span>
                    <span className="min-w-[110px] text-right text-[10px] tracking-[0.25em] uppercase font-semibold text-bronze">
                      {row.r}
                    </span>
                  </div>
                ))}
              </div>

              {/* Footer stats */}
              <div className="grid grid-cols-3 border-t border-white/10 pt-4 text-center">
                {[
                  ["+30", "Empresas no mesmo painel"],
                  ["6", "Tarefas que somem da sua lista"],
                  ["100%", "Dos seus dados protegidos"],
                ].map(([v, l]) => (
                  <div key={l} className="border-r border-white/5 last:border-r-0">
                    <div className="font-display font-bold text-lg md:text-xl text-bronze">
                      {v}
                    </div>
                    <div className="text-[9px] tracking-[0.25em] uppercase text-white/75 mt-0.5">
                      {l}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   CONTRAST
   ============================================================ */
interface ContrastProps {
  brandName?: string;
  sectionNumber?: string;
}

function Contrast({ brandName, sectionNumber = "05 — CONTRASTE" }: ContrastProps) {
  const less = [
    "noites de fechamento",
    "planilha que ninguém entende",
    "cliente cobrando resposta",
    "achismo virando decisão",
    "um login pra cada empresa",
  ];
  const more = [
    "fechamento em minutos",
    "resposta na hora",
    "cliente confiando no dado",
    "decisão com números reais",
    "carteira inteira em um lugar",
  ];

  return (
    <section className="relative py-20 md:py-24 px-6 lg:px-10 border-b border-border text-left">
      <div data-nosnippet="" className="absolute top-14 right-6 lg:right-10 text-[11px] font-display font-semibold tracking-[0.35em] text-bronze uppercase">
        {sectionNumber}
      </div>
      <div className="max-w-[1360px] mx-auto grid md:grid-cols-2 gap-0 border border-border mt-10">
        <div className="p-10 md:p-14 border-b md:border-b-0 md:border-r border-border">
          <div className="text-[10px] uppercase tracking-[0.25em] text-text-3 mb-6 font-semibold">
            O que você deixa para trás
          </div>
          <h3 className="font-display font-bold text-2xl md:text-3xl mb-8 text-foreground">
            A rotina amarrada no operacional
          </h3>
          <ul className="space-y-4">
            {less.map((item) => (
              <li key={item} className="flex items-center gap-3 text-sm text-text-3">
                <span className="w-1.5 h-1.5 rounded-full bg-border-strong" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="p-10 md:p-14 bg-surface-2">
          <div className="text-[10px] uppercase tracking-[0.25em] text-bronze mb-6 font-semibold">
            O que você ganha com a {brandName || 'Seven'}
          </div>
          <h3 className="font-display font-bold text-2xl md:text-3xl mb-8 text-foreground">
            Clareza para crescer
          </h3>
          <ul className="space-y-4">
            {more.map((item) => (
              <li key={item} className="flex items-center gap-3 text-sm font-medium text-foreground">
                <Check className="w-4 h-4 text-bronze shrink-0" strokeWidth={2.5} />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   PLANS
   ============================================================ */
interface PlansProps {
  isWhiteLabel?: boolean;
  tenantSlug?: string;
  brandName?: string;
  sectionNumber?: string;
  isExecutive?: boolean;
  onOpenDemoModal?: () => void;
}

function Plans({
  isWhiteLabel = false,
  tenantSlug,
  brandName = "Seven Analytics",
  sectionNumber = "06 — PLANOS",
  isExecutive: _isExecutive = false,
  onOpenDemoModal,
}: PlansProps) {
  const { trackEvent } = useTracking();
  const navigate = useNavigate();

  const FALLBACK_PLANS: SubscriptionPlan[] = [
    {
      id: "183f6b8f-e509-4b31-a3b9-ea3bbae298b7",
      name: "Connect",
      price: 99.90,
      setup_fee: 99.90,
      max_bank_accounts: 1,
      max_payments: 0,
      max_billets: 0,
      max_invoices: 0,
      max_companies: 1,
      active: true,
      allowed_modules: []
    },
    {
      id: "d115eaf0-b1b5-40c3-99e2-d101a07a6876",
      name: "Start",
      price: 199.90,
      setup_fee: 199.90,
      max_bank_accounts: 2,
      max_payments: 30,
      max_billets: 30,
      max_invoices: 30,
      max_companies: 2,
      active: true,
      allowed_modules: []
    },
    {
      id: "1f17f4d8-a1ea-4463-b605-d8aaf5d2c0d6",
      name: "Growth",
      price: 399.90,
      setup_fee: 399.90,
      max_bank_accounts: 3,
      max_payments: 100,
      max_billets: 80,
      max_invoices: 80,
      max_companies: 5,
      active: true,
      allowed_modules: []
    },
    {
      id: "bb8fb77a-ab62-4620-84e5-964ac1f5a1ad",
      name: "Scale",
      price: 799.90,
      setup_fee: 799.90,
      max_bank_accounts: 4,
      max_payments: 250,
      max_billets: 200,
      max_invoices: 200,
      max_companies: 10,
      active: true,
      allowed_modules: []
    },
    {
      id: "9ae70718-8773-4964-9e82-4d991403243b",
      name: "Enterprise",
      price: 1599.90,
      setup_fee: 1599.90,
      max_bank_accounts: 8,
      max_payments: 600,
      max_billets: 500,
      max_invoices: 500,
      max_companies: -1,
      active: true,
      allowed_modules: []
    }
  ];

  const [plans, setPlans] = useState<SubscriptionPlan[]>(isWhiteLabel ? [] : FALLBACK_PLANS);
  const [loading, setLoading] = useState(isWhiteLabel);
  const [isOffline, setIsOffline] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'MONTHLY' | 'ANNUAL'>('MONTHLY');

  useEffect(() => {
    async function loadPlans() {
      try {
        if (isWhiteLabel) {
          setLoading(true);
        }
        let data: SubscriptionPlan[] = [];
        if (isWhiteLabel && tenantSlug) {
          data = await billingService.getPublicPlansBySlug(tenantSlug);
        } else {
          data = await billingService.getActivePlans();
        }

        if (data && Array.isArray(data) && data.length > 0) {
          const validatedData = data.map((item) => subscriptionPlanSchema.parse(item));
          setPlans(validatedData);
          setIsOffline(false);
        } else if (!isWhiteLabel) {
          setPlans(FALLBACK_PLANS);
          setIsOffline(true);
        } else {
          setPlans([]);
          setIsOffline(false);
        }
      } catch (err) {
        console.warn("[Plans Integration] Fallback/Error handling:", err);
        if (!isWhiteLabel) {
          setPlans(FALLBACK_PLANS);
          setIsOffline(true);
        } else {
          setPlans([]);
        }
      } finally {
        setLoading(false);
      }
    }
    loadPlans();
  }, [isWhiteLabel, tenantSlug]);

  if (loading) {
    return (
      <section
        id="planos"
        className="relative py-20 md:py-24 px-6 lg:px-10 border-b border-border text-left"
      >
        <div className="absolute top-14 right-6 lg:right-10 text-[11px] font-display font-semibold tracking-[0.35em] text-bronze uppercase">
          {sectionNumber}
        </div>

        <div className="max-w-[1360px] mx-auto">
          <div className="max-w-3xl mb-16">
            <h2 className="font-display font-bold text-4xl md:text-5xl leading-[0.98] tracking-[-0.03em]">
              Um plano para cada <br />
              <span className="text-bronze">tamanho de operação.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-0 border-l border-t border-border pointer-events-none select-none">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="relative border-r border-b border-border p-7 flex flex-col bg-surface min-h-[430px] animate-pulse"
              >
                <div className="h-3 w-16 bg-border-strong mb-6 rounded" />
                <div className="h-8 w-24 bg-border-strong mb-8 rounded" />
                <div className="space-y-3 flex-1">
                  <div className="h-3 w-full bg-border-strong/60 rounded" />
                  <div className="h-3 w-5/6 bg-border-strong/60 rounded" />
                  <div className="h-3 w-4/5 bg-border-strong/60 rounded" />
                  <div className="h-3 w-2/3 bg-border-strong/60 rounded" />
                </div>
                <div className="h-10 w-full bg-border-strong mt-6 rounded" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (plans.length === 0) {
    return (
      <section
        id="planos"
        className="relative py-20 md:py-24 px-6 lg:px-10 border-b border-border text-left"
      >
        <div className="absolute top-14 right-6 lg:right-10 text-[11px] font-display font-semibold tracking-[0.35em] text-bronze uppercase">
          {sectionNumber}
        </div>

        <div className="max-w-[1360px] mx-auto">
          <div className="max-w-3xl mb-12">
            <h2 className="font-display font-bold text-4xl md:text-5xl leading-[0.98] tracking-[-0.03em]">
              Planos Comerciais <br />
              <span className="text-bronze">Sob Medida.</span>
            </h2>
            <p className="mt-4 text-text-3 text-base sm:text-lg">
              Para a organização {brandName}, os pacotes de BPO financeiro e gestão são estruturados de forma personalizada conforme o volume e porte da sua empresa.
            </p>
          </div>

          <div className="border border-border bg-surface p-8 sm:p-12 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="space-y-2">
              <h3 className="font-display font-bold text-xl text-foreground">
                Solicite uma Proposta Personalizada
              </h3>
              <p className="text-sm text-text-3 max-w-xl">
                Entre em contato diretamente com nossa equipe de especialistas para desenhar o plano ideal para a sua operação.
              </p>
            </div>
            {onOpenDemoModal && (
              <Button
                onClick={onOpenDemoModal}
                className="bg-bronze hover:bg-bronze-hover text-white px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] shrink-0 cursor-pointer"
              >
                Falar com Especialista
              </Button>
            )}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="planos"
      className="relative py-20 md:py-24 px-6 lg:px-10 border-b border-border text-left"
    >
      <div className="absolute top-14 right-6 lg:right-10 text-[11px] font-display font-semibold tracking-[0.35em] text-bronze uppercase">
        {sectionNumber}
      </div>

      <div className="max-w-[1360px] mx-auto">
        <div className="max-w-3xl mb-10">
          <h2 className="font-display font-bold text-4xl md:text-5xl leading-[0.98] tracking-[-0.03em]">
            Um plano para cada <br />
            <span className="text-bronze">tamanho de operação.</span>
          </h2>
          {isOffline && (
            <p className="mt-2 text-xs text-text-3 font-semibold uppercase tracking-wider">
              Modo Offline ativo • Planos oficiais do banco de dados carregados
            </p>
          )}
        </div>

        {/* Segmented Control: Mensal vs Anual (-20% OFF) */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex p-1 bg-surface-2 border border-border rounded-xl shadow-xs">
            <button
              type="button"
              onClick={() => setBillingCycle('MONTHLY')}
              className={`px-5 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all cursor-pointer ${
                billingCycle === 'MONTHLY'
                  ? 'bg-surface text-foreground shadow-xs border border-border'
                  : 'text-text-3 hover:text-foreground'
              }`}
            >
              Mensal
            </button>
            <button
              type="button"
              onClick={() => setBillingCycle('ANNUAL')}
              className={`px-5 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-all cursor-pointer ${
                billingCycle === 'ANNUAL'
                  ? 'bg-surface text-foreground shadow-xs border border-border'
                  : 'text-text-3 hover:text-foreground'
              }`}
            >
              <span>Anual</span>
              <span className="bg-[var(--success,#059669)] text-white text-[9px] font-bold px-2 py-0.5 rounded-full">
                20% OFF
              </span>
            </button>
          </div>
        </div>

        <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 ${plans.length >= 6 ? 'lg:grid-cols-6' : plans.length === 4 ? 'lg:grid-cols-4' : plans.length === 3 ? 'lg:grid-cols-3' : plans.length === 2 ? 'lg:grid-cols-2' : plans.length === 1 ? 'lg:grid-cols-1 max-w-md' : 'lg:grid-cols-5'} gap-0 border-l border-t border-border`}>
          {plans.map((p) => {
            const isStart = p.name.toLowerCase() === 'start';
            const features = getPlanFeatures(p);
            const allowAnnual = p.allow_annual_billing !== false;
            const isAnnualSelected = billingCycle === 'ANNUAL' && allowAnnual;
            const monthlyBase = Number(p.price);
            const discountPct = p.annual_discount_percentage !== undefined && p.annual_discount_percentage !== null
              ? Number(p.annual_discount_percentage)
              : 20.00;
            const displayedMonthlyPrice = isAnnualSelected
              ? (monthlyBase * (1 - discountPct / 100)).toFixed(2).replace('.', ',')
              : monthlyBase.toFixed(2).replace('.', ',');
            const annualTotal = (monthlyBase * 12 * (1 - discountPct / 100)).toFixed(2).replace('.', ',');
            const annualSavings = (monthlyBase * 12 * (discountPct / 100)).toFixed(2).replace('.', ',');

            return (
              <div
                key={p.id || p.name}
                className={`relative p-7 flex flex-col ${
                  isStart
                    ? "pricing-card-popular-highlight text-white z-10 lg:-my-4 lg:scale-[1.02]"
                    : "border-r border-b border-border bg-surface"
                }`}
              >
                {isStart && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-bronze text-white text-[9px] font-bold px-3 py-1 uppercase tracking-[0.25em]">
                    Mais escolhido
                  </span>
                )}
                <h3
                  className={`font-display font-bold text-[11px] uppercase tracking-[0.3em] mb-4 ${
                    isStart ? "text-bronze" : "text-text-3"
                  }`}
                >
                  {p.name}
                </h3>

                <div>
                  {isAnnualSelected && (
                    <div className={`text-xs line-through mb-1 ${isStart ? "text-white/75" : "text-text-3"}`}>
                      De R$ {monthlyBase.toFixed(2).replace('.', ',')}/mês
                    </div>
                  )}
                  <div>
                    <span
                      className={`text-xs ${isStart ? "text-white/75" : "text-text-3"}`}
                    >
                      R$
                    </span>
                    <span
                      className={`font-display font-bold text-4xl mx-1 ${
                        isStart ? "text-white" : "text-foreground"
                      }`}
                    >
                      {displayedMonthlyPrice}
                    </span>
                    <span
                      className={`text-xs ${isStart ? "text-white/75" : "text-text-3"}`}
                    >
                      /mês
                    </span>
                  </div>
                  {isAnnualSelected ? (
                    <div className={`text-[11px] mt-1.5 font-medium ${isStart ? "text-white/80" : "text-text-2"}`}>
                      Faturado anualmente por R$ {annualTotal}/ano <br />
                      <span className={isStart ? "text-emerald-300 font-semibold" : "text-[var(--success,#059669)] font-semibold"}>
                        (Economia de R$ {annualSavings}/ano)
                      </span>
                    </div>
                  ) : billingCycle === 'ANNUAL' && !allowAnnual ? (
                    <div className="text-[11px] mt-1.5 text-text-3 font-medium">
                      Apenas Ciclo Mensal
                    </div>
                  ) : null}
                </div>

                {/* Pílula / Badge em Destaque do Banco */}
                <div
                  className={`my-5 py-2 px-3 rounded-lg border text-[11px] font-display font-bold uppercase tracking-wider flex items-center justify-center gap-2 ${
                    isStart
                      ? "bg-bronze text-white border-bronze/40 shadow-sm"
                      : "bg-bronze/10 text-bronze border-bronze/25"
                  }`}
                >
                  <Landmark className="w-3.5 h-3.5 shrink-0" />
                  <span>{getBankBadgeText(p.max_bank_accounts)}</span>
                </div>

                <ul
                  className={`space-y-3 text-xs sm:text-sm mb-8 flex-1 ${
                    isStart ? "text-white/85" : "text-text-2"
                  }`}
                >
                  {features.map((f) => (
                    <li key={f} className="flex gap-2.5">
                      <Check
                        className="w-3.5 h-3.5 text-bronze shrink-0 mt-0.5"
                        strokeWidth={2.5}
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => {
                    trackEvent({ eventType: 'PLAN_VIEW', elementId: p.name });
                    const cycleQuery = isAnnualSelected ? '&cycle=annual' : '';
                    const checkoutUrl = isWhiteLabel && tenantSlug 
                      ? `/checkout?plan=${p.id}&tenant=${tenantSlug}${cycleQuery}` 
                      : `/checkout?plan=${p.id}${isAnnualSelected ? '&cycle=annual' : ''}`;
                    navigate(checkoutUrl);
                  }}
                  className={`w-full py-3 text-xs font-semibold uppercase tracking-[0.15em] transition-all cursor-pointer ${
                    isStart
                      ? "bg-bronze hover:bg-bronze-hover text-white shadow-md"
                      : "bg-bronze/90 hover:bg-bronze text-white"
                  }`}
                >
                  Assinar
                </Button>
              </div>
            );
          })}
        </div>

        {/* Rodapé Informativo sobre Assinatura por Empresa */}
        <div className="mt-10 p-4 rounded-xl border border-border bg-surface-2/60 flex items-start sm:items-center gap-3 text-xs text-text-3 max-w-4xl mx-auto">
          <Info className="w-4 h-4 text-bronze shrink-0 mt-0.5 sm:mt-0" />
          <span>
            * O valor da assinatura é por empresa (CNPJ/CPF). No mesmo painel você pode gerenciar múltiplas empresas alternando de conta com 1 clique.
          </span>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   BPO ESSENCIAL
   ============================================================ */
interface BpoEssencialProps {
  brandName: string;
  contactPhone: string | null;
  contactEmail?: string | null;
  isExecutive?: boolean;
  sectionNumber?: string;
}

function formatWhatsAppNumber(phone?: string | null): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (!digits) return null;
  if ((digits.length === 10 || digits.length === 11) && !digits.startsWith('55')) {
    return `55${digits}`;
  }
  return digits;
}

function BpoEssencial({
  brandName,
  contactPhone,
  contactEmail,
  isExecutive = false,
  sectionNumber = "07 — BPO FINANCEIRO",
}: BpoEssencialProps) {
  const { trackEvent } = useTracking();
  const bullets = [
    "Contas a pagar e receber de forma organizada",
    "Gestão completa de cobranças e pagamentos",
    "Conciliações diárias e relatórios de desempenho",
    "Análise de fluxo de caixa projetado para tomada de decisão",
    "E muito mais para escalar seu negócio...",
  ];

  const cleanPhone = formatWhatsAppNumber(contactPhone);
  const whatsappMsg = encodeURIComponent(
    `Olá! Gostaria de agendar um diagnóstico e solicitar uma proposta de BPO Financeiro com a ${brandName}.`
  );

  return (
    <section
      id="bpo-essencial"
      className="relative py-20 md:py-24 px-6 lg:px-10 border-b border-border bg-surface-2 text-left"
    >
      <div data-nosnippet="" className="absolute top-14 right-6 lg:right-10 text-[11px] font-display font-semibold tracking-[0.35em] text-bronze uppercase">
        {sectionNumber}
      </div>

      <div className="max-w-[1360px] mx-auto">
        <div data-nosnippet="" className="text-[11px] font-display font-semibold tracking-[0.35em] text-bronze uppercase mb-4">
          {isExecutive ? "SOLUÇÃO PERSONALIZADA & BPO ESTRATÉGICO" : "SOLUÇÃO PERSONALIZADA"}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
          {/* Coluna Esquerda */}
          <div>
            <h2 data-nosnippet="" className="font-display font-bold text-3xl md:text-4xl lg:text-5xl text-foreground tracking-[-0.03em] leading-tight mb-4">
              {isExecutive ? "BPO FINANCEIRO DE ALTA PERFORMANCE" : "BPO FINANCEIRO"}
            </h2>
            <p className="text-text-3 font-sans text-base md:text-lg mb-8">
              Organize e impulsione seu departamento financeiro com a excelência e assessoria da {brandName}.
            </p>
            <ul className="space-y-4 mb-8">
              {bullets.map((bullet) => (
                <li key={bullet} className="flex items-start gap-3 text-sm md:text-base font-medium text-foreground">
                  <Check className="w-5 h-5 text-bronze shrink-0 mt-0.5" />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
            <div className="p-4 bg-surface border border-border text-xs md:text-sm text-text-2 leading-relaxed">
              Como cada empresa possui uma demanda de volume, transações e complexidade diferente, a equipe da {brandName} monta uma proposta personalizada sob medida para a sua operação.
            </div>
          </div>

          {/* Coluna Direita */}
          <div>
            <div className="p-8 md:p-10 bg-surface border border-border-strong flex flex-col justify-between space-y-6 shadow-sm">
              <div>
                <h3 className="font-display font-bold text-xl md:text-2xl text-foreground mb-3">
                  Pronto para profissionalizar sua gestão financeira com a {brandName}?
                </h3>
                <p className="text-text-3 text-sm leading-relaxed">
                  Agende um diagnóstico com nossos consultores especialistas e receba um plano de atendimento exclusivo para o seu modelo de negócio.
                </p>
              </div>
              {cleanPhone ? (
                <a
                  href={`https://wa.me/${cleanPhone}?text=${whatsappMsg}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent({ eventType: 'CTA_CLICK', elementId: 'bpo_essential_cta' })}
                  className="inline-flex items-center justify-center gap-2 bg-bronze hover:bg-bronze-hover text-white py-4 px-8 text-xs md:text-sm font-bold uppercase tracking-[0.25em] transition-all w-full text-center border-0 cursor-pointer"
                >
                  <span>Solicitar Orçamento Personalizado</span>
                  <ArrowRight className="w-4 h-4 shrink-0" />
                </a>
              ) : contactEmail ? (
                <a
                  href={`mailto:${contactEmail}?subject=${encodeURIComponent(`Solicitação de Diagnóstico BPO - ${brandName}`)}`}
                  onClick={() => trackEvent({ eventType: 'CTA_CLICK', elementId: 'bpo_essential_cta' })}
                  className="inline-flex items-center justify-center gap-2 bg-bronze hover:bg-bronze-hover text-white py-4 px-8 text-xs md:text-sm font-bold uppercase tracking-[0.25em] transition-all w-full text-center border-0 cursor-pointer"
                >
                  <span>Solicitar Orçamento Personalizado</span>
                  <ArrowRight className="w-4 h-4 shrink-0" />
                </a>
              ) : (
                <Button
                  onClick={() => {
                    trackEvent({ eventType: 'CTA_CLICK', elementId: 'bpo_essential_cta' });
                    openDemoModal();
                  }}
                  className="inline-flex items-center justify-center gap-2 bg-bronze hover:bg-bronze-hover text-white py-4 px-8 text-xs md:text-sm font-bold uppercase tracking-[0.25em] transition-all w-full text-center border-0 cursor-pointer"
                >
                  <span>Solicitar Orçamento Personalizado</span>
                  <ArrowRight className="w-4 h-4 shrink-0" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   CTA
   ============================================================ */
interface CTAProps {
  isWhiteLabel: boolean;
  brandName: string;
  contactPhone?: string | null;
  contactEmail?: string | null;
  ctaText?: string | null;
  isExecutive?: boolean;
  onCtaClick?: () => void;
}

function CTA({
  isWhiteLabel,
  brandName,
  contactPhone,
  contactEmail,
  ctaText,
  isExecutive = false,
  onCtaClick,
}: CTAProps) {
  const { trackEvent } = useTracking();
  const cleanPhone = formatWhatsAppNumber(contactPhone);
  const whatsappMsg = encodeURIComponent(
    `Olá! Gostaria de falar com um especialista e solicitar uma proposta para a ${brandName}.`
  );
  const primaryButtonLabel =
    ctaText ||
    (isExecutive
      ? 'Agendar Diagnóstico Financeiro'
      : isWhiteLabel
      ? 'Acessar Plataforma'
      : 'Agendar demonstração');

  return (
    <section
      id="contato"
      className="relative py-20 md:py-24 px-6 lg:px-10 bg-ink text-white overflow-hidden text-center"
    >
      <div className="absolute inset-0 pointer-events-none cta-glow" />
      <div className="relative max-w-[1360px] mx-auto text-center space-y-8">
        <div data-nosnippet="" className="text-[11px] font-display font-semibold tracking-[0.35em] text-bronze uppercase">
          {isExecutive ? 'PRONTO PARA PROFISSIONALIZAR?' : 'PRONTO PARA COMEÇAR?'}
        </div>
        <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-[-0.03em] text-white text-center">
          {isExecutive ? (
            <>
              Seu financeiro estratégico,
              <br />
              <span className="text-bronze">com governança de alto nível.</span>
            </>
          ) : (
            <>
              Seu financeiro estratégico,
              <br />
              <span className="text-bronze">a partir de hoje.</span>
            </>
          )}
        </h2>
        <p className="cta-desc max-w-xl mx-auto text-white/80 text-lg leading-relaxed text-center">
          {isWhiteLabel
            ? `Fale com nossos especialistas e veja como a ${brandName} pode organizar, automatizar e gerar inteligência para a sua operação.`
            : 'Fale com um de nossos especialistas e veja como a Seven Analytics pode organizar, automatizar e gerar inteligência para a sua operação.'}
        </p>
        <div className="flex flex-wrap justify-center gap-4 pt-4">
          <Button
            onClick={() => {
              trackEvent({ eventType: 'CTA_CLICK', elementId: 'cta_demo' });
              if (onCtaClick) {
                onCtaClick();
              } else {
                openDemoModal();
              }
            }}
            className="group inline-flex items-center gap-2 bg-bronze hover:bg-bronze-hover text-white px-8 py-4 text-[13px] font-semibold tracking-wide transition-all"
          >
            {primaryButtonLabel}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition" />
          </Button>
          {cleanPhone ? (
            <a
              href={`https://wa.me/${cleanPhone}?text=${whatsappMsg}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent({ eventType: 'CTA_CLICK', elementId: 'whatsapp_contact' })}
              className="inline-flex items-center gap-2 border border-white/20 text-white px-8 py-4 text-[13px] font-semibold tracking-wide hover:bg-white/5 transition"
            >
              Falar no WhatsApp
            </a>
          ) : contactEmail ? (
            <a
              href={`mailto:${contactEmail}?subject=${encodeURIComponent(`Contato Comercial - ${brandName}`)}`}
              onClick={() => trackEvent({ eventType: 'CTA_CLICK', elementId: 'email_contact' })}
              className="inline-flex items-center gap-2 border border-white/20 text-white px-8 py-4 text-[13px] font-semibold tracking-wide hover:bg-white/5 transition"
            >
              Falar por E-mail
            </a>
          ) : null}
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   FOOTER
   ============================================================ */
interface FooterProps {
  isWhiteLabel: boolean;
  brandName: string;
  tenantSlug: string;
  loginUrl: string;
  contactEmail: string;
  contactPhone: string | null;
  isExecutive?: boolean;
}

function Footer({
  isWhiteLabel,
  brandName,
  tenantSlug,
  loginUrl,
  contactEmail,
  contactPhone,
  isExecutive = false,
}: FooterProps) {
  const privacyUrl = isWhiteLabel && tenantSlug ? `/privacidade?tenant=${tenantSlug}` : '/privacidade';
  const termsUrl = isWhiteLabel && tenantSlug ? `/termos?tenant=${tenantSlug}` : '/termos';
  const deletionUrl = isWhiteLabel && tenantSlug ? `/exclusao-conta?tenant=${tenantSlug}` : '/exclusao-conta';

  const navLinks = isExecutive
    ? [
        { label: 'Governança', href: '#governanca' },
        { label: 'Plataforma', href: '#plataforma' },
        { label: 'Assistente', href: '#inteligencia' },
        { label: 'BPO Financeiro', href: '#bpo-essencial' },
        { label: 'Contato', href: '#contato' },
      ]
    : [
        { label: 'Plataforma', href: '#plataforma' },
        { label: 'Assistente', href: '#inteligencia' },
        { label: 'Operação', href: '#bpo' },
        ...(!isWhiteLabel ? [{ label: 'Planos', href: '#planos' }] : []),
        { label: 'BPO Financeiro', href: '#bpo-essencial' },
        { label: 'Contato', href: '#contato' },
      ];

  return (
    <footer className="border-t border-border bg-surface-2 pt-12 pb-24 md:pt-16 md:pb-28 px-6 lg:px-10 text-[13px] text-text-3 text-left">
      <div className="max-w-[1360px] mx-auto grid grid-cols-1 md:grid-cols-4 gap-10">
        <div>
          <h3 className="font-display font-bold text-[11px] uppercase tracking-[0.28em] text-foreground mb-3">
            {brandName}
          </h3>
          <p className="leading-relaxed">
            {isExecutive
              ? 'BPO Financeiro de alta performance, inteligência estratégica e governança contínua para empresas em expansão.'
              : 'Inteligência financeira e controle operacional completo para prestadores de serviços, contadores e BPOs.'}
          </p>
        </div>
        <div>
          <h3 className="font-display font-bold text-[11px] uppercase tracking-[0.28em] text-foreground mb-3">Navegação</h3>
          <ul className="flex flex-col gap-2">
            {navLinks.map((link) => (
              <li key={link.label}>
                <a href={link.href} className="block text-text-2 hover:text-foreground">
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="font-display font-bold text-[11px] uppercase tracking-[0.28em] text-foreground mb-3">Acesso</h3>
          <ul className="flex flex-col gap-2">
            <li><Link to={loginUrl} className="block text-text-2 hover:text-foreground">Entrar no painel</Link></li>
            {!isWhiteLabel ? (
              <li><Link to="/register" className="block text-text-2 hover:text-foreground">Criar conta</Link></li>
            ) : (
              <li><a href="#bpo-essencial" className="block text-text-2 hover:text-foreground">Solicitar proposta</a></li>
            )}
          </ul>
        </div>
        <div>
          <h3 className="font-display font-bold text-[11px] uppercase tracking-[0.28em] text-foreground mb-3">Contato</h3>
          <ul className="flex flex-col gap-2">
            <li><a href={`mailto:${contactEmail}`} className="block text-text-2 hover:text-foreground break-all">{contactEmail}</a></li>
            {contactPhone && <li><span className="block font-medium text-foreground">{contactPhone}</span></li>}
            <li><span className="block">Seg. a Sex., 9h às 18h</span></li>
          </ul>
        </div>
      </div>
      <div className="max-w-[1360px] mx-auto mt-16 pt-8 border-t border-border flex flex-wrap justify-between items-center gap-4 text-xs">
        <span className="flex items-center">© {new Date().getFullYear()} {brandName}. Todos os direitos reservados.</span>
        <div className="flex items-center gap-6">
          <a href="#" className="text-text-2 hover:text-foreground">Compliance</a>
          <Link to={privacyUrl} className="text-text-2 hover:text-foreground">Privacidade</Link>
          <Link to={termsUrl} className="text-text-2 hover:text-foreground">Termos</Link>
          <Link to={deletionUrl} className="text-text-2 hover:text-foreground">Exclusão de Conta</Link>
        </div>
      </div>
    </footer>
  );
}
